/**
 * Historial de contraseñas (F3b): últimas versiones, visibles solo para quien
 * tenía acceso cuando estaban vigentes (o el owner actual). Cada apertura se
 * audita como reveal.
 */
import { useState } from 'react';
import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { errMsg, useCopy, withUnlockRetry } from '@/hooks/useVault';
import { useCountdown } from '@/hooks/useCountdown';
import { VAULT_POLICY } from '../../../../shared/types/vault';
import { fmtRelative } from './vault-ui';

interface HistoryPanelProps {
  secretId: string;
  ensureUnlocked: () => Promise<boolean>;
}

export function HistoryPanel({ secretId, ensureUnlocked }: HistoryPanelProps) {
  const { t, i18n } = useTranslation();
  const copy = useCopy();
  const versions = trpc.vault.listSecretVersions.useQuery({ secretId }, { staleTime: 30_000 });
  const reveal = trpc.vault.revealSecretVersion.useMutation();
  const [shown, setShown] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  const open = async (version: number) => {
    setError(null);
    try {
      const res = await withUnlockRetry(ensureUnlocked, () => reveal.mutateAsync({ secretId, version }));
      if (!res) return;
      setShown((s) => ({ ...s, [version]: res.password }));
    } catch (e) {
      setError(errMsg(e as { message?: string }));
    }
  };

  const items = versions.data ?? [];
  if (!items.length) return <p className="text-sm text-muted-foreground">{t('vault.history.empty', 'Sin rotaciones todavía.')}</p>;

  return (
    <div className="space-y-2">
      <ul className="divide-y">
        {items.map((v) => (
          <li key={v.version} className="flex items-center gap-3 py-2 text-sm">
            <History className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">v{v.version}</div>
              <div className="text-xs text-muted-foreground">
                {t('vault.history.rotated', 'rotada {{when}} por {{who}}', { when: fmtRelative(v.rotatedAt, i18n.language), who: v.rotatedBy.split('@')[0] })}
              </div>
            </div>
            {shown[v.version] ? (
              <RevealedVersion value={shown[v.version]} onCopy={() => copy(shown[v.version])} onHide={() => setShown((s) => { const { [v.version]: _drop, ...rest } = s; return rest; })} />
            ) : v.canView ? (
              <Button variant="outline" size="sm" onClick={() => open(v.version)} disabled={reveal.isPending}>
                {t('vault.history.show', 'Ver')}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">{t('vault.history.no_access', 'sin acceso entonces')}</span>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Una contraseña vieja revelada: se oculta sola a los 30 s (mismo hook que Revelar y TOTP). */
function RevealedVersion({ value, onCopy, onHide }: { value: string; onCopy: () => void; onHide: () => void }) {
  const { t } = useTranslation();
  const left = useCountdown(VAULT_POLICY.revealSeconds, value, onHide);
  return (
    <button type="button" className="font-mono text-xs underline decoration-dotted" onClick={onCopy} title={t('vault.reveal.copy', 'Copiar')} aria-label={t('vault.reveal.copy', 'Copiar')}>
      {value} <span className="text-muted-foreground no-underline">({left} s)</span>
    </button>
  );
}
